import { ArrowLeft } from 'lucide-react'
import Image from 'next/image'
import Link from 'next/link'
import type { ReactNode } from 'react'
import { Icon, cn } from '@/components/ui'
import { AMBIANCES, type Ambiance, type Reglage } from './ambiances'

/**
 * Le héros de l'Oasis — DA 2027 (lot 3, 2026-10-27).
 *
 * Un aplat rose : la légende et le titre à gauche, la phrase d'intro et les
 * actions à droite, puis **la photo de l'onglet en pleine largeur dans
 * l'aplat**. C'est la composition validée par Baptiste sur les maquettes
 * (`../oasis-2027-maquettes/`) ; le panneau de verre sombre posé sur la photo
 * disparaît, et avec lui ses mesures de contraste — aucun texte ne se pose plus
 * sur la photo.
 *
 * Trois formats :
 *
 * - `principal` (accueil) : le panorama, `clamp(320px, 34vw, 68vh)` de haut ;
 * - `section` (onglets) : `clamp(260px, 28vw, 52vh)` ;
 * - **fiche** — sans `ambiance` : pas de photo, et un lien `retour` au-dessus du
 *   titre (fiche replay, fiche séance, signature…).
 *
 * ⚠️ **Titres courts et directs, majuscule en début de phrase** (décisions de
 * Baptiste). `titre` + `accent` : le mot en Playfair, point final compris.
 *
 * ⚠️ `pulsation` n'a plus d'effet : la pastille animée de la V4 n'existe plus en
 * 2027. La propriété reste acceptée le temps que les écrans passent au lot 4.
 *
 * ⚠️ Le titre est un `<h1>` : un seul héros par écran.
 */
export function BandeauHero({
  annonce,
  titre,
  accent,
  intro,
  taille = 'section',
  ambiance,
  retour,
  children,
}: {
  /** La légende au-dessus du titre (monospace, capitales). */
  annonce: string
  titre: string
  accent: string
  intro?: ReactNode
  taille?: 'principal' | 'section'
  /** Sans effet depuis la DA 2027 — voir l'en-tête. */
  pulsation?: boolean
  /** La photo de l'onglet. Absente : héros de fiche, sans photo. */
  ambiance?: Ambiance
  /** Le lien de retour d'une fiche (« Tous les replays »). */
  retour?: { href: string; libelle: string }
  children?: ReactNode
}) {
  const decor: Reglage | undefined = ambiance ? AMBIANCES[ambiance] : undefined
  const principal = taille === 'principal'
  const fiche = !decor

  return (
    <section
      className={cn(
        'relative grid gap-4 rounded-[24px] bg-rose p-[32px_22px_22px]',
        'oasis:items-end oasis:gap-x-12 oasis:gap-y-5 oasis:p-[clamp(32px,3.2vw,72px)]',
        fiche ? 'oasis:grid-cols-1' : 'oasis:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]',
      )}
    >
      <div className="min-w-0">
        {retour && (
          <Link
            href={retour.href}
            className="mb-4 inline-flex min-h-11 items-center gap-2 text-[14px] font-bold text-ink"
          >
            <Icon as={ArrowLeft} size={16} />
            {retour.libelle}
          </Link>
        )}
        <span className="u-legende block text-ink">{annonce}</span>
        <h1 className="u-t27 mt-4 text-[54px] text-ink oasis:text-[clamp(56px,5.6vw,132px)]">
          {titre} <span className="u-accent">{accent}</span>
        </h1>
      </div>

      {(intro || children) && (
        <div className="min-w-0">
          {intro && (
            <p className="max-w-[34em] text-[16px] leading-[1.55] text-ink text-pretty oasis:text-[clamp(16px,1.15vw,22px)]">
              {intro}
            </p>
          )}
          {children && <div className="mt-6 flex flex-wrap items-center gap-[18px_22px]">{children}</div>}
        </div>
      )}

      {decor && (
        <div
          className={cn(
            'relative mt-3 overflow-hidden rounded-[16px] oasis:col-span-2 oasis:rounded-[24px]',
            principal
              ? 'aspect-[4/3.4] oasis:aspect-auto oasis:h-[clamp(320px,34vw,68vh)]'
              : 'h-[200px] oasis:h-[clamp(260px,28vw,52vh)]',
          )}
        >
          {/* Photo décorative : l'écran ne perd rien sans elle. `fill` + `sizes`
              plein écran, et `zoom` l'agrandit à 160 % calée à gauche. */}
          <Image
            src={decor.fichier}
            alt=""
            fill
            priority
            placeholder={decor.flou ? 'blur' : 'empty'}
            blurDataURL={decor.flou}
            sizes="100vw"
            className={cn('object-cover', decor.zoom && '!w-[160%] !max-w-none')}
            style={{ objectPosition: decor.cadrage }}
          />
        </div>
      )}
    </section>
  )
}
